import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { staffInvite, type StaffInviteInfo } from '../api/staffInvite'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { AboutButton, VerSportsLogo } from '@versports/ui'
import { ClubMark } from '../components/ClubMark'

const ROLE_LABEL: Record<StaffInviteInfo['club_role'], string> = {
  manager: 'Manager',
}

export type StaffInvitePath =
  /** Hay sesión y es la persona invitada: acepta con la cuenta que ya tiene. */
  | 'accept'
  /** Hay sesión, pero de otra cuenta: cerrar sesión y volver. */
  | 'wrong-session'
  /** Sin sesión y el mail invitado ya es usuario: iniciar sesión y aceptar. */
  | 'login-existing'
  /** Sin sesión y sin cuenta previa: alta. */
  | 'signup'

/**
 * Qué puede hacer quien abrió el link, según la invitación y la sesión.
 *
 * Vive separado del render porque es donde estaba el bug que reportó mg
 * (2026-09-10): el único camino era `signup`, así que a un invitado cuyo mail
 * ya era usuario se le ofrecía crear una cuenta que ya existía y el alta
 * terminaba en 409.
 *
 * `sessionEmail` es el mail (o username) de la sesión abierta, o null si no
 * hay. Una invitación sin `email` no nombra a nadie — link para pasar a mano
 * —, así que cualquier sesión sirve para aceptarla.
 */
export function staffInvitePath(
  info: Pick<StaffInviteInfo, 'email' | 'account_exists'>,
  sessionEmail: string | null,
): StaffInvitePath {
  if (sessionEmail !== null) {
    if (!info.email || info.email === sessionEmail.toLowerCase()) return 'accept'
    return 'wrong-session'
  }
  return info.account_exists ? 'login-existing' : 'signup'
}

/**
 * Aterrizaje de los links de invitación de staff (`/staff-invite/:token`).
 *
 * A diferencia de `/invite/:token` (que solo resuelve y bouncea a login),
 * este token ES la credencial de alta: el form registra la cuenta directo
 * contra `POST /staff-invites/{token}/register`, que crea el user + fila
 * ClubStaff con el rol de la invitación y loguea (cookie session) de una.
 *
 * El alta no es el único camino desde el 2026-09-10: si el mail invitado YA
 * es un usuario, el backend lo dice en `account_exists` y acá se ofrece
 * iniciar sesión y aceptar (`POST .../accept`) en vez del form. Antes el
 * único camino era `register`, que 409ea con "ya existe un usuario con ese
 * email": el invitado veía "creá una cuenta" para una cuenta que ya tenía y
 * no tenía forma de aceptar (reporte de mg sobre el invite de Edu, que abrió
 * el link en incógnito y quedó trabado ahí).
 *
 * Si quien abre el link YA tiene una sesión activa hay dos casos. Si es la
 * persona invitada, acepta ahí mismo con un botón. Si es otra cuenta (el
 * superadmin que generó el link y lo clickea para probar, un jugador en un
 * device compartido), no lo mandamos silenciosamente a `/club` — el token
 * sigue pendiente y esa persona nunca se entera de que tiene que cerrar
 * sesión —: mostramos la card + el botón de logout (mismo patrón que
 * `InvitePage` para "otro email"), y tras el logout aparece el camino que
 * corresponda en la misma página.
 */
export function StaffInvitePage() {
  const { token = '' } = useParams<{ token: string }>()
  const { user, status, refresh, logout } = useAuth()
  const navigate = useNavigate()

  const [info, setInfo] = useState<StaffInviteInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [loggingOut, setLoggingOut] = useState(false)
  const [logoutError, setLogoutError] = useState<string | null>(null)

  const [accepting, setAccepting] = useState(false)
  const [acceptError, setAcceptError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    staffInvite
      .resolve(token)
      .then((data) => {
        if (cancelled) return
        setInfo(data)
        if (data.email) setEmail(data.email)
      })
      .catch((e) => {
        if (cancelled) return
        const msg =
          e instanceof ApiError && e.status === 404
            ? 'Esta invitación no existe.'
            : e instanceof ApiError
              ? e.message
              : 'Error de red.'
        setLoadError(msg)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  if (status === 'hydrating' || status === 'idle')
    return <Shell><p className="text-sm text-text-muted">Cargando…</p></Shell>

  if (loadError) {
    return (
      <Shell>
        <div className="banner-err">{loadError}</div>
        <p className="mt-md text-sm text-text-muted">
          Pedile a quien te invitó que te comparta un link nuevo.
        </p>
        <p className="mt-md text-sm">
          <Link to="/login">Ir al login →</Link>
        </p>
      </Shell>
    )
  }

  if (info === null) {
    return <Shell><p className="text-sm text-text-muted">Cargando…</p></Shell>
  }

  if (info.status !== 'pending') {
    return (
      <Shell>
        <h1 className="m-0">Invitación no disponible</h1>
        <p className="mt-md text-sm text-text-muted">
          {info.status === 'expired'
            ? 'Esta invitación venció.'
            : info.status === 'used'
              ? 'Esta invitación ya fue usada para crear una cuenta.'
              : 'Estado desconocido.'}
        </p>
        <p className="mt-md text-sm">
          Si ya tenés cuenta, podés <Link to="/login">entrar al login</Link>.
        </p>
      </Shell>
    )
  }

  if (status === 'authenticated' && user) {
    const sessionLabel = user.email ?? user.username
    const hasClub = (user.club_memberships?.length ?? 0) > 0
    // La sesión abierta sirve para aceptar si es la persona invitada, o si la
    // invitación no nombra a nadie (link para pasar a mano). Con la sesión de
    // otro, el backend rechaza igual: mejor decirlo acá que después del click.
    const isInvitee = !info.email || info.email === sessionLabel?.toLowerCase()

    const onAccept = async () => {
      setAccepting(true)
      setAcceptError(null)
      try {
        await staffInvite.accept(token)
        await refresh()
        navigate('/club', { replace: true })
      } catch (e) {
        setAcceptError(e instanceof ApiError ? e.message : 'Error de red.')
        setAccepting(false)
      }
    }

    const onLogout = async () => {
      setLoggingOut(true)
      setLogoutError(null)
      try {
        await logout()
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : 'Error de red.'
        setLogoutError(msg)
        setLoggingOut(false)
      }
    }

    return (
      <Shell>
        <div className="flex items-center gap-md">
          <ClubMark logoUrl={info.club.logo_url} name={info.club.name} size={40} />
          <div className="min-w-0">
            <h1 className="m-0 break-words text-lg font-bold">{info.club.name}</h1>
            <span className="tag text-xs">{ROLE_LABEL[info.club_role]}</span>
          </div>
        </div>
        <p className="mt-md text-sm text-text-muted">
          {isInvitee ? (
            <>
              Estás con la sesión de <strong>{sessionLabel}</strong> abierta.
              Aceptá y quedás como <strong>{ROLE_LABEL[info.club_role]}</strong> de{' '}
              {info.club.name} con esta misma cuenta.
            </>
          ) : (
            <>
              Esta invitación es para <strong>{info.email}</strong> y estás con la
              sesión de <strong>{sessionLabel}</strong> abierta: cerrá sesión para
              usarla.
            </>
          )}
        </p>

        {acceptError && <div className="mt-md banner-err">{acceptError}</div>}
        {logoutError && <div className="mt-md banner-err">{logoutError}</div>}

        {isInvitee ? (
          <button
            type="button"
            onClick={onAccept}
            disabled={accepting}
            className={[
              'mt-md rounded px-md py-sm text-base font-medium transition-colors',
              accepting
                ? 'bg-surface-elevated text-text-primary opacity-60'
                : 'bg-primary text-primary-fg hover:brightness-110',
            ].join(' ')}
          >
            {accepting ? '…' : 'Aceptar la invitación'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onLogout}
            disabled={loggingOut}
            className={[
              'mt-md rounded px-md py-sm text-base font-medium transition-colors',
              loggingOut
                ? 'bg-surface-elevated text-text-primary opacity-60'
                : 'bg-primary text-primary-fg hover:brightness-110',
            ].join(' ')}
          >
            {loggingOut ? '…' : 'Cerrar sesión y continuar'}
          </button>
        )}

        <p className="mt-md text-center text-sm">
          {hasClub ? (
            <Link to="/club">Ir al panel</Link>
          ) : (
            <Link to="/">Ir al inicio</Link>
          )}
        </p>
      </Shell>
    )
  }

  // El caso que dejaba trabado al invitado: su mail ya es usuario, así que el
  // form de alta sólo puede terminar en 409. El camino es iniciar sesión y
  // aceptar — el `next` trae de vuelta a esta misma página, donde la rama de
  // arriba ya ofrece el botón de aceptar.
  if (staffInvitePath(info, null) === 'login-existing') {
    return (
      <Shell>
        <div className="flex items-center gap-md">
          <ClubMark logoUrl={info.club.logo_url} name={info.club.name} size={40} />
          <div className="min-w-0">
            <h1 className="m-0 break-words text-lg font-bold">{info.club.name}</h1>
            <span className="tag text-xs">{ROLE_LABEL[info.club_role]}</span>
          </div>
        </div>
        <p className="mt-md text-sm text-text-muted">
          Te invitaron a ser <strong>{ROLE_LABEL[info.club_role]}</strong> de{' '}
          {info.club.name}. Ya tenés una cuenta de VerSports con{' '}
          <strong>{info.email}</strong>: entrá con ella y la invitación se suma a
          esa misma cuenta, no hace falta crear otra.
        </p>
        <Link
          to={`/login?next=${encodeURIComponent(`/staff-invite/${token}`)}`}
          className="mt-md rounded bg-primary px-md py-sm text-center text-base font-medium text-primary-fg no-underline transition-colors hover:brightness-110"
        >
          Iniciar sesión y aceptar
        </Link>
        <p className="mt-md text-center text-xs text-text-muted">
          Si no te acordás la contraseña, recuperala desde el login: el link de
          invitación te espera acá.
        </p>
      </Shell>
    )
  }

  const canSubmit = email.includes('@') && password.length >= 6 && !busy

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setSubmitError(null)
    try {
      await staffInvite.register(token, {
        email: email.trim().toLowerCase(),
        password,
        display_name: displayName.trim() || undefined,
      })
      await refresh()
      // No mostramos el gate de T&C acá: redirigimos a /club, que ya está
      // envuelto en `RequireStaffTerms` (router.tsx) y va a mostrar
      // `StaffTermsGate` de una — un manager recién registrado nunca tiene
      // `terms_ok`. Evita duplicar la lógica del gate en dos lugares
      // (decisión: opción "dejar que el gate de ClubPage se encargue", ver
      // pedido de mg 2026-07-09).
      navigate('/club', { replace: true })
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Error de red.'
      setSubmitError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Shell>
      <div className="flex items-center gap-md">
        <ClubMark logoUrl={info.club.logo_url} name={info.club.name} size={40} />
        <div className="min-w-0">
          <h1 className="m-0 break-words text-lg font-bold">{info.club.name}</h1>
          <span className="tag text-xs">{ROLE_LABEL[info.club_role]}</span>
        </div>
      </div>
      <p className="mt-sm text-sm text-text-muted">
        Te invitaron a ser <strong>{ROLE_LABEL[info.club_role]}</strong> de{' '}
        {info.club.name} en VerSports. Creá tu cuenta para empezar.
      </p>

      <form onSubmit={onSubmit} className="mt-md flex flex-col gap-[10px]">
        {submitError && <div className="banner-err">{submitError}</div>}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@dominio.com"
          autoComplete="email"
          className="input"
          disabled={busy}
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="contraseña (6+)"
          autoComplete="new-password"
          className="input"
          disabled={busy}
        />
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="nombre para mostrar (opcional)"
          maxLength={64}
          className="input"
          disabled={busy}
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className={[
            'rounded px-md py-sm text-base font-medium transition-colors',
            canSubmit
              ? 'bg-primary text-primary-fg hover:brightness-110'
              : 'bg-surface-elevated text-text-primary opacity-60',
          ].join(' ')}
        >
          {busy ? '…' : 'Crear cuenta y entrar'}
        </button>
      </form>

      <p className="mt-md text-center text-xs text-text-muted">
        ¿Ya tenés cuenta con otro email? Entrá con ella desde el{' '}
        <Link to={`/login?next=${encodeURIComponent(`/staff-invite/${token}`)}`}>
          login
        </Link>{' '}
        y aceptá la invitación desde ahí.
      </p>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-surface to-surface-elevated">
      <div className="flex flex-1 items-center justify-center px-lg py-2xl">
        <div className="panel flex max-w-md flex-col gap-md p-xl">
          <VerSportsLogo size="lg" className="self-center" />
          {children}
        </div>
      </div>
      <AboutButton />
    </div>
  )
}
